import { useEffect, useRef } from "react";
import { useRouterState } from "@tanstack/react-router";
import { hasAdsConsent, onConsentChange } from "@/lib/consent";

const PIXEL_ID = "RiQeM4SL2s6kWhB1qTL417";
const SDK_URL = "https://bzrcdn.openai.com/sdk/oaiq.min.js";
const DEBUG = import.meta.env.DEV;

declare global {
  interface Window {
    oaiq?: ((...args: unknown[]) => void) & { q?: unknown[] };
    __oaiqReady?: boolean;
  }
}

/** Ad landing pages that report a page_viewed measurement event. */
const AD_LANDING_PATHS = new Set([
  "/services",
  "/es/servicios",
  "/shopify-migration",
  "/es/migracion-shopify",
]);

/** Routes that never load the pixel (private/app areas). */
const PRIVATE_PREFIX =
  /^\/(?:admin|portal|agency|agency-login|partner-login|login|reset-password|checkout|order-confirmed)(?:\/|$)/;

/** Loads the SDK once, in the current page, only after ad-measurement consent. */
function ensurePixel(): boolean {
  if (typeof window === "undefined") return false;
  if (!hasAdsConsent()) return false;
  if (window.__oaiqReady) return true;

  if (!window.oaiq) {
    const q = function (...args: unknown[]) {
      q.q.push(args);
    } as ((...args: unknown[]) => void) & { q: unknown[] };
    q.q = [];
    window.oaiq = q;
    const script = document.createElement("script");
    script.async = true;
    script.src = SDK_URL;
    const first = document.getElementsByTagName("script")[0];
    if (first?.parentNode) first.parentNode.insertBefore(script, first);
    else document.head.appendChild(script);
  }
  window.oaiq("init", { pixelId: PIXEL_ID, debug: DEBUG });
  window.__oaiqReady = true;
  return true;
}

/**
 * Reports a confirmed lead exactly once per submission.
 * `eventId` must be the stable server-issued record id so a retry, reload or
 * revisit can never be counted twice.
 */
export function trackPixelLead(eventId: string): void {
  if (typeof window === "undefined" || !eventId) return;
  if (!ensurePixel()) return;
  const key = `oaiq.lead.${eventId}`;
  try {
    if (window.localStorage.getItem(key)) return;
    window.localStorage.setItem(key, "1");
  } catch {
    /* storage unavailable: still send once for this page view */
  }
  window.oaiq?.("measure", "lead_created", { type: "customer_action" }, { event_id: eventId });
}

/**
 * Single pixel instance for the public site. Initializes once after consent
 * and reports one page_viewed per actual landing/SPA navigation on the ad
 * landing pages. No personal data is sent.
 */
export function OpenAIPixelTracker() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const lastReported = useRef<string | null>(null);
  const lastTitle = useRef<string | null>(null);

  useEffect(() => {
    let timer: number | undefined;
    let attempts = 0;

    const report = () => {
      lastReported.current = pathname;
      lastTitle.current = document.title;
      window.oaiq?.("measure", "page_viewed", {
        type: "contents",
        contents: [{ id: pathname, name: document.title, content_type: "page" }],
      });
    };

    const run = () => {
      if (PRIVATE_PREFIX.test(pathname) || !AD_LANDING_PATHS.has(pathname)) {
        lastReported.current = null;
        return;
      }
      if (!ensurePixel()) return;
      if (lastReported.current === pathname) return;
      // The route head updates the document title after render, so on a
      // client-side navigation the title can still be the previous page's.
      // Wait briefly for it to change before reporting.
      if (lastTitle.current !== null && document.title === lastTitle.current && attempts < 20) {
        attempts += 1;
        window.clearTimeout(timer);
        timer = window.setTimeout(run, 100);
        return;
      }
      report();
    };

    run();
    const off = onConsentChange(run);
    return () => {
      window.clearTimeout(timer);
      off();
    };
  }, [pathname]);


  return null;
}

export default OpenAIPixelTracker;
